import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, CheckCircle } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

export default function AuthModal({ open, onOpenChange }) {
  const { sendMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!email.trim() || !email.includes("@")) {
      setError("Enter a valid email");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await sendMagicLink(email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send link");
    }
    setBusy(false);
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) {
      setSent(false);
      setEmail("");
      setError("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="backdrop-blur-xl rounded-sm max-w-[400px]"
        style={{ background: "var(--sdb-glass)", border: "1px solid var(--sdb-border)" }}
      >
        <DialogHeader>
          <div className="font-mono text-[10px] tracking-[3px] uppercase mb-1" style={{ color: "var(--sdb-accent)" }}>
            Sign In
          </div>
          <DialogTitle className="text-xl font-semibold" style={{ color: "var(--sdb-text)" }}>
            {sent ? "Check your email" : "Log in to MockDB"}
          </DialogTitle>
          <DialogDescription style={{ color: "var(--sdb-text-secondary)" }} className="text-sm">
            {sent ? `We sent a login link to ${email}` : "Enter your email to receive a magic login link."}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="flex flex-col items-center py-6 gap-3">
            <CheckCircle className="w-12 h-12" style={{ color: "var(--sdb-accent)" }} />
            <p className="font-mono text-xs text-center" style={{ color: "var(--sdb-text-muted)" }}>
              Click the link in your email to log in. It expires in 15 minutes.
            </p>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider" style={{ color: "var(--sdb-text-muted)" }}>
                Email
              </Label>
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                className="mt-1.5 w-full px-3 py-2.5 rounded-sm text-sm outline-none"
                style={{ background: "var(--sdb-bg)", border: `1px solid ${error ? "var(--sdb-error)" : "var(--sdb-border)"}`, color: "var(--sdb-text)" }}
              />
              {error && <p className="text-[10px] mt-1 font-mono" style={{ color: "var(--sdb-error)" }}>{error}</p>}
            </div>
            <Button
              onClick={handleSubmit}
              disabled={busy}
              className="w-full gradient-bg text-black font-semibold text-sm rounded-sm py-3 h-auto glow-btn hover:text-black"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Mail className="w-4 h-4 mr-2" />Send Login Link</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
